import React, { useState } from 'react';
import {
  Title,
  Group,
  Button,
  Text,
  Stack,
  Card,
  Switch,
  Select,
  Divider,
  Alert,
  ActionIcon,
  Badge,
  Grid,
  TextInput,
  Tabs,
  LoadingOverlay,
} from '@mantine/core';
import {
  IconBell,
  IconMail,
  IconPhone,
  IconChevronLeft,
  IconCheck,
  IconAlertTriangle,
  IconClock,
  IconPhoto,
  IconActivity,
  IconCalendar,
  IconDeviceMobile,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useUpdateNotificationPreferences } from '../../hooks/useParent';

interface NotificationPreferences {
  daily_reports: boolean;
  activity_updates: boolean;
  photo_uploads: boolean;
  safety_alerts: boolean;
  teacher_messages: boolean;
  schedule_changes: boolean;
  email_notifications: boolean;
  sms_notifications: boolean;
  push_notifications: boolean;
}

const NotificationSettings: React.FC = () => {
  const navigate = useNavigate();
  const updatePreferences = useUpdateNotificationPreferences();
  
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    daily_reports: true,
    activity_updates: true,
    photo_uploads: true,
    safety_alerts: true,
    teacher_messages: true,
    schedule_changes: true,
    email_notifications: true,
    sms_notifications: false,
    push_notifications: true,
  });

  const [quietHours, setQuietHours] = useState({
    enabled: true,
    start: '20:00',
    end: '08:00',
  });

  const [emailFrequency, setEmailFrequency] = useState('immediate');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [activeTab, setActiveTab] = useState('notifications');

  const handlePreferenceChange = (key: keyof NotificationPreferences, value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = () => {
    updatePreferences.mutate(preferences);
  };

  const notificationTypes = [
    {
      key: 'daily_reports' as const,
      title: 'Daily Reports',
      description: 'Receive summary of your child\'s day including activities, meals, and notes',
      icon: <IconCalendar size={20} />,
      color: 'blue',
      priority: 'high',
    },
    {
      key: 'activity_updates' as const,
      title: 'Activity Updates',
      description: 'Get notified when your child participates in new activities',
      icon: <IconActivity size={20} />,
      color: 'green',
      priority: 'medium',
    },
    {
      key: 'photo_uploads' as const,
      title: 'New Photos',
      description: 'Notifications when new photos of your child are uploaded',
      icon: <IconPhoto size={20} />,
      color: 'pink',
      priority: 'medium',
    },
    {
      key: 'safety_alerts' as const,
      title: 'Safety Alerts',
      description: 'Critical alerts about your child\'s safety and wellbeing',
      icon: <IconAlertTriangle size={20} />,
      color: 'red',
      priority: 'critical',
    },
    {
      key: 'teacher_messages' as const,
      title: 'Teacher Messages',
      description: 'Direct messages and updates from your child\'s teacher',
      icon: <IconMail size={20} />,
      color: 'indigo',
      priority: 'high',
    },
    {
      key: 'schedule_changes' as const,
      title: 'Schedule Changes',
      description: 'Updates about changes to class schedules, events, or closures',
      icon: <IconClock size={20} />,
      color: 'orange',
      priority: 'high',
    },
  ];

  const deliveryMethods = [
    {
      key: 'push_notifications' as const,
      title: 'Push Notifications',
      description: 'Instant notifications on your device',
      icon: <IconBell size={20} />,
      available: true,
    },
    {
      key: 'email_notifications' as const,
      title: 'Email Notifications',
      description: 'Receive notifications via email',
      icon: <IconMail size={20} />,
      available: true,
    },
    {
      key: 'sms_notifications' as const,
      title: 'SMS Notifications',
      description: 'Text message notifications for urgent alerts',
      icon: <IconPhone size={20} />,
      available: true,
    },
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'blue';
      default: return 'gray';
    }
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <Card withBorder>
        <Group justify="space-between">
          <Group>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={() => navigate('/parent/dashboard')}
            >
              <IconChevronLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={2}>Notification Settings</Title>
              <Text size="sm" c="dimmed">
                Manage how and when you receive updates about your child
              </Text>
            </div>
          </Group>
          <Button
            leftSection={<IconCheck size={16} />}
            onClick={handleSave}
            loading={updatePreferences.isPending}
          >
            Save Changes
          </Button>
        </Group>
      </Card>

      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'notifications')}>
        <Tabs.List>
          <Tabs.Tab value="notifications" leftSection={<IconBell size={16} />}>
            Notification Types
          </Tabs.Tab>
          <Tabs.Tab value="delivery" leftSection={<IconDeviceMobile size={16} />}>
            Delivery Methods
          </Tabs.Tab>
          <Tabs.Tab value="schedule" leftSection={<IconClock size={16} />}>
            Schedule & Timing
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="notifications" pt="md">
          <Stack gap="md">
            <Alert color="blue" title="Notification Types">
              Choose which types of updates you'd like to receive about your child's activities and progress.
            </Alert>

            {notificationTypes.map((type) => (
              <Card key={type.key} withBorder>
                <Group justify="space-between">
                  <Group>
                    <div style={{ color: `var(--mantine-color-${type.color}-6)` }}>
                      {type.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <Group gap="xs" mb="xs">
                        <Text fw={500}>{type.title}</Text>
                        <Badge 
                          size="xs" 
                          color={getPriorityColor(type.priority)}
                          variant="light"
                        >
                          {type.priority}
                        </Badge>
                      </Group>
                      <Text size="sm" c="dimmed">
                        {type.description}
                      </Text>
                    </div>
                  </Group>
                  <Switch
                    checked={preferences[type.key]}
                    onChange={(event) => 
                      handlePreferenceChange(type.key, event.currentTarget.checked)
                    }
                    color={type.color}
                    size="md"
                  />
                </Group>
              </Card>
            ))}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="delivery" pt="md">
          <Stack gap="md">
            <Alert color="green" title="Delivery Methods">
              Choose how you want to receive notifications. You can enable multiple methods for different types of alerts.
            </Alert>

            {deliveryMethods.map((method) => (
              <Card key={method.key} withBorder>
                <Group justify="space-between">
                  <Group>
                    {method.icon}
                    <div>
                      <Text fw={500}>{method.title}</Text>
                      <Text size="sm" c="dimmed">
                        {method.description}
                      </Text>
                    </div>
                  </Group>
                  <Switch
                    checked={preferences[method.key]}
                    onChange={(event) => 
                      handlePreferenceChange(method.key, event.currentTarget.checked)
                    }
                    disabled={!method.available}
                    size="md"
                  />
                </Group>
              </Card>
            ))}

            <Divider my="md" />

            {/* Email Settings */}
            {preferences.email_notifications && (
              <Card withBorder>
                <Title order={5} mb="md">Email Settings</Title>
                <Stack gap="md">
                  <Select
                    label="Email Frequency"
                    description="How often should we send email notifications?"
                    data={[
                      { value: 'immediate', label: 'Immediate (as they happen)' },
                      { value: 'hourly', label: 'Hourly digest' },
                      { value: 'daily', label: 'Daily summary' },
                      { value: 'weekly', label: 'Weekly summary' },
                    ]}
                    value={emailFrequency}
                    onChange={(value) => setEmailFrequency(value || 'immediate')}
                  />
                </Stack>
              </Card>
            )}

            {/* SMS Settings */}
            {preferences.sms_notifications && (
              <Card withBorder>
                <Title order={5} mb="md">SMS Settings</Title>
                <Stack gap="md">
                  <TextInput
                    label="Phone Number"
                    description="We'll only use this for urgent notifications"
                    placeholder="+1 (555) 123-4567"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    leftSection={<IconPhone size={16} />}
                  />
                  <Text size="xs" c="dimmed">
                    SMS notifications are reserved for critical safety alerts and urgent messages from teachers.
                  </Text>
                </Stack>
              </Card>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="schedule" pt="md">
          <Stack gap="md">
            <Alert color="violet" title="Notification Schedule">
              Set quiet hours and customize when you receive notifications to avoid disruptions.
            </Alert>

            <Card withBorder>
              <Group justify="space-between" mb="md">
                <div>
                  <Title order={5}>Quiet Hours</Title>
                  <Text size="sm" c="dimmed">
                    Disable non-urgent notifications during these hours
                  </Text>
                </div>
                <Switch
                  checked={quietHours.enabled}
                  onChange={(event) => 
                    setQuietHours(prev => ({ ...prev, enabled: event.currentTarget.checked }))
                  }
                  size="md"
                />
              </Group>

              {quietHours.enabled && (
                <Grid>
                  <Grid.Col span={6}>
                    <TextInput
                      label="Start Time"
                      type="time"
                      value={quietHours.start}
                      onChange={(e) => 
                        setQuietHours(prev => ({ ...prev, start: e.target.value }))
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput
                      label="End Time"
                      type="time"
                      value={quietHours.end}
                      onChange={(e) => 
                        setQuietHours(prev => ({ ...prev, end: e.target.value }))
                      }
                    />
                  </Grid.Col>
                </Grid>
              )}
            </Card>

            <Card withBorder>
              <Title order={5} mb="md">Priority Override</Title>
              <Text size="sm" c="dimmed" mb="md">
                These notifications will always come through, even during quiet hours:
              </Text>
              <Stack gap="xs">
                <Group>
                  <IconAlertTriangle size={16} color="red" />
                  <Text size="sm">Safety alerts and emergencies</Text>
                </Group>
                <Group>
                  <IconMail size={16} color="blue" />
                  <Text size="sm">Urgent messages from teachers</Text>
                </Group>
                <Group>
                  <IconClock size={16} color="orange" />
                  <Text size="sm">Schedule changes affecting pickup/dropoff</Text>
                </Group>
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={5} mb="md">Weekly Schedule</Title>
              <Text size="sm" c="dimmed" mb="md">
                Customize which days you receive different types of notifications:
              </Text>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm">Daily reports</Text>
                  <Badge variant="light" color="blue">Monday - Friday</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Activity updates</Text>
                  <Badge variant="light" color="green">Every day</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Photo uploads</Text>
                  <Badge variant="light" color="pink">Every day</Badge>
                </Group>
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* Quick Actions */}
      <Card withBorder>
        <Title order={4} mb="md">Quick Actions</Title>
        <Group>
          <Button
            variant="light"
            leftSection={<IconCheck size={16} />}
            onClick={() => {
              setPreferences({
                daily_reports: true,
                activity_updates: true,
                photo_uploads: true,
                safety_alerts: true,
                teacher_messages: true,
                schedule_changes: true,
                email_notifications: true,
                sms_notifications: false,
                push_notifications: true,
              });
              notifications.show({
                title: 'Preset Applied',
                message: 'Recommended settings have been applied',
                color: 'green',
              });
            }}
          >
            Use Recommended Settings
          </Button>
          <Button
            variant="light"
            color="red"
            onClick={() => {
              setPreferences({
                daily_reports: false,
                activity_updates: false,
                photo_uploads: false,
                safety_alerts: true,
                teacher_messages: true,
                schedule_changes: true,
                email_notifications: false,
                sms_notifications: false,
                push_notifications: false,
              });
              notifications.show({
                title: 'Minimized Notifications',
                message: 'Only essential notifications will be sent',
                color: 'orange',
              });
            }}
          >
            Minimize Notifications
          </Button>
        </Group>
      </Card>

      <LoadingOverlay visible={updatePreferences.isPending} />
    </Stack>
  );
};

export default NotificationSettings;